import moment from "moment";
import { handleErrorMessage } from "helpers/methods";
import {
  downloadSignedDocument,
  getRenewableCounts,
  getRenewableDocumentsByDate,
} from "./RenewableService";

import {
  Avatar,
  Tooltip,
  IconButton,
  Stack,
  Typography,
  colors,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { getAvatarColors } from "utils/avatarColors";
  import { focusRing } from "theme/colors";
import history from "utils/history";

export const getRenewableGraphData = async (
  months: number,
  setIsLoading: (loading: boolean) => void,
  setChartData: (data: any[]) => void,
) => {
  try {
    setIsLoading(true);
    const response = await getRenewableCounts(months);

    if (response) {
      const formattedData = Object.keys(response).map((date) => ({
        displayDate: moment(date).format("MMM YYYY"),
        count: response[date],
        originalDate: date,
      }));

      setChartData(formattedData);
    }
  } catch (error: any) {
    handleErrorMessage(error);
  } finally {
    setIsLoading(false);
  }
};

const handleDownload = async (envelopeId: string, fileName: string) => {
  try {
    const response = await downloadSignedDocument(envelopeId, fileName);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName.includes(".pdf") ? fileName : `${fileName}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error: any) {
    handleErrorMessage(error);
  }
};

export const fetchDocumentsByMonths = async (
  startDate: string,
  setIsTableLoading: (loading: boolean) => void,
  setDocumentList: (date: any[]) => void,
) => {
  try {
    setIsTableLoading(true);
    const response = await getRenewableDocumentsByDate(startDate);

    if (response) {
      const MAX_LENGTH = 30;
      // The API returns a LIST, one entry per renewal row ({ fileName, request }).
      // It used to be an object keyed by file name, which silently merged two
      // envelopes whose files shared a name -- the row then showed one document's
      // name beside another document's signer, dates and download.
      const dateArray = (response as any[]).map((item: any) => {
        const doc = item.request;
        const key = item.fileName;
        const signer = doc.signers?.[0];

        const truncatedFileName =
          key.length > MAX_LENGTH ? `${key.substring(0, MAX_LENGTH)}...` : key;

        const targetFile = doc.files?.find(
          (file: any) => file.unsignedPdf === key || file.signedPdf === key,
        );

        const terminationDate = targetFile?.terminationDate;

        const avatarName =
          signer?.name
            ?.split(" ")
            ?.map((word: any) => word[0])
            ?.join("")
            .toUpperCase() || "?";

        return {
          rowId: doc.id,
          fileName: (
            <Tooltip
              title={key.length > MAX_LENGTH ? key : ""}
              arrow
              placement="bottom-start"
            >
              {/*
                Rendered as a real button so the row action is reachable by
                keyboard and exposed as a control (WCAG 2.1.1, 4.1.2). The
                accessible name carries the untruncated file name.
              */}
              <Typography
                component="button"
                type="button"
                variant="body2"
                fontWeight={500}
                aria-label={`${key}. View document details`}
                sx={{
                  cursor: "pointer",
                  backgroundColor: "transparent",
                  border: 0,
                  p: 0,
                  color: "inherit",
                  textAlign: "left",
                  "&:hover": { textDecoration: "underline" },
                  "&:focus-visible": {
                    outline: `2px solid ${focusRing}`,
                    outlineOffset: "2px",
                  },
                }}
                onClick={() => {
                  history.push(`/requests/view/${doc.id}`);
                }}
              >
                {truncatedFileName}
              </Typography>
            </Tooltip>
          ),
          signer: (
            <Tooltip title={signer?.email ?? ""} arrow>
              {/*
                Initials alone carry no meaning for assistive tech, and the
                email was only reachable on hover — the avatar is now a named
                image and focusable so the tooltip opens on focus too.
              */}
              <Avatar
                role="img"
                tabIndex={0}
                aria-label={`Signer: ${signer?.name ?? "Unknown"}${
                  signer?.email ? `, ${signer.email}` : ""
                }`}
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: "0.85rem",
                  bgcolor: getAvatarColors(signer.name?.[0] || "A").bg,
                  color: getAvatarColors(signer.name?.[0] || "A").color,
                  "&:focus-visible": {
                    outline: `2px solid ${focusRing}`,
                    outlineOffset: "2px",
                  },
                }}
              >
                {avatarName}
              </Avatar>
            </Tooltip>
          ),
          requestedBy: (
            <Stack>
              <Typography variant="body2">{doc.requestedBy?.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {doc.requestedBy?.email}
              </Typography>
            </Stack>
          ),
          createdOn: (
            <Typography variant="body2">
              {doc.createdOn
                ? moment(doc.createdOn).format("MMMM D, YYYY")
                : "-"}
            </Typography>
          ),
          terminationDate: (
            // `error.main` (#F04438) only reaches 3.76:1 on the table surface —
            // `error.dark` keeps the same hue at 6.57:1 (WCAG 1.4.3).
            <Typography variant="body2" color="error.dark">
              {terminationDate
                ? moment(terminationDate).format("MMMM D, YYYY")
                : "-"}
            </Typography>
          ),
          action: (
            <Tooltip title={`Download ${key}`} arrow>
              <IconButton
                size="small"
                aria-label={`Download ${key}`}
                onClick={() => handleDownload(doc.id, targetFile.signedPdf)}
                sx={{
                  "&:focus-visible": {
                    outline: `2px solid ${focusRing}`,
                    outlineOffset: "2px",
                  },
                }}
              >
                <FileDownloadOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
        };
      });
      setDocumentList(dateArray);
    }
  } catch (error: any) {
    handleErrorMessage(error);
  } finally {
    setIsTableLoading(false);
  }
};
